import React, { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import useScrollLock from '../../../hooks/useScrollLock';
import api from '../../../api';

interface EditTaskModalProps {
    open: boolean;
    initialText: string;
    initialDescription?: string;
    initialStatus?: 'todo' | 'inprogress' | 'done';
    roomId?: string;
    taskId?: string;
    onClose: () => void;
    onSave: (newText: string, newDescription?: string, newStatus?: 'todo' | 'inprogress' | 'done') => void;
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
    open,
    initialText,
    initialDescription = '',
    initialStatus = 'todo',
    roomId,
    taskId,
    onClose,
    onSave,
}) => {
    const [taskText, setTaskText] = useState(initialText);
    const [taskDesc, setTaskDesc] = useState(initialDescription);
    const [taskStatus, setTaskStatus] = useState(initialStatus);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useScrollLock(open);

    useEffect(() => {
        setTaskText(initialText);
        setTaskDesc(initialDescription || '');
        setTaskStatus(initialStatus || 'todo');
        setSaveError(null);
    }, [initialText, initialDescription, initialStatus, open]);

    const handleSave = async () => {
        if (!taskText.trim()) return;

        setSaving(true);
        setSaveError(null);

        try {
            // Call PATCH API when roomId and taskId are provided
            if (roomId && taskId) {
                await api.teamMode.updateTask(roomId, taskId, {
                    title: taskText.trim(),
                    description: taskDesc.trim() || undefined,
                    status: taskStatus,
                });
            }
            onSave(taskText.trim(), taskDesc.trim() || undefined, taskStatus);
            onClose();
        } catch (err: any) {
            setSaveError(err?.response?.data?.message || 'Failed to update task. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-xl">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-medium text-gray-900">Edit Task</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Task Description
                        </label>
                        <input
                            type="text"
                            value={taskText}
                            onChange={(e) => setTaskText(e.target.value)}
                            placeholder="What do you need to do?"
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base"
                            autoFocus
                            onKeyPress={(e) => e.key === 'Enter' && handleSave()}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <textarea
                            value={taskDesc}
                            onChange={(e) => setTaskDesc(e.target.value)}
                            placeholder="Add more details..."
                            rows={3}
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 resize-none text-base"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                        </label>
                        <select
                            value={taskStatus}
                            onChange={(e) => setTaskStatus(e.target.value as 'todo' | 'inprogress' | 'done')}
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base"
                        >
                            <option value="todo">To Do</option>
                            <option value="inprogress">In Progress</option>
                            <option value="done">Done</option>
                        </select>
                    </div>

                    {saveError && (
                        <p className="text-sm text-red-500 text-center -mt-2">{saveError}</p>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="flex-1 px-4 py-3 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving || !taskText.trim() || (taskText.trim() === initialText && taskDesc.trim() === (initialDescription || '') && taskStatus === initialStatus)}
                            className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm ${saving || !taskText.trim() || (taskText.trim() === initialText && taskDesc.trim() === (initialDescription || '') && taskStatus === initialStatus)
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-700 text-white'
                                }`}
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditTaskModal;
